import { useState, useEffect } from 'react'

interface CurrencyInfo {
  localPrice: string | null
  currencyCode: string | null
  currencySymbol: string | null
  countryName: string | null
  countryCode: string | null
  isLoading: boolean
  error: boolean
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  ARS: 'ARS$',
  MXN: 'MX$',
  COP: 'COP$',
  CLP: 'CLP$',
  PEN: 'S/',
  UYU: 'UYU$',
  BRL: 'R$',
  EUR: '€',
  GBP: '£',
  BOB: 'Bs',
  PYG: '₲',
  CRC: '₡',
  GTQ: 'Q',
  HNL: 'L',
  NIO: 'C$',
  PAB: 'B/.',
  DOP: 'RD$',
  VES: 'Bs.S',
}

const COUNTRY_TO_CURRENCY: Record<string, string> = {
  AR: 'ARS',
  MX: 'MXN',
  CO: 'COP',
  CL: 'CLP',
  PE: 'PEN',
  UY: 'UYU',
  BR: 'BRL',
  ES: 'EUR',
  GB: 'GBP',
  FR: 'EUR',
  DE: 'EUR',
  IT: 'EUR',
  PT: 'EUR',
  BO: 'BOB',
  PY: 'PYG',
  CR: 'CRC',
  GT: 'GTQ',
  HN: 'HNL',
  NI: 'NIO',
  PA: 'PAB',
  DO: 'DOP',
  VE: 'VES',
  EC: 'USD',
  SV: 'USD',
}

export function useCurrencyConversion(usdAmount: number): CurrencyInfo {
  const [info, setInfo] = useState<CurrencyInfo>({
    localPrice: null,
    currencyCode: null,
    currencySymbol: null,
    countryName: null,
    countryCode: null,
    isLoading: true,
    error: false,
  })

  useEffect(() => {
    let cancelled = false

    async function fetchConversion() {
      try {
        // Step 1: Detect country via IP
        const geoRes = await fetch('https://ipapi.co/json/')
        if (!geoRes.ok) throw new Error('Geo failed')
        const geoData = await geoRes.json()

        const countryCode: string = geoData.country_code || ''
        const countryName: string = geoData.country_name || ''
        const currencyCode = COUNTRY_TO_CURRENCY[countryCode]

        // If country is US or uses USD, or not in our map, skip conversion
        if (!currencyCode || currencyCode === 'USD') {
          if (!cancelled) {
            setInfo({
              localPrice: null,
              currencyCode: null,
              currencySymbol: null,
              countryName,
              countryCode,
              isLoading: false,
              error: false,
            })
          }
          return
        }

        // Step 2: Get exchange rate
        const rateRes = await fetch('https://open.er-api.com/v6/latest/USD')
        if (!rateRes.ok) throw new Error('Rate failed')
        const rateData = await rateRes.json()

        const rate = rateData.rates?.[currencyCode]
        if (!rate) throw new Error('No rate for ' + currencyCode)

        const converted = usdAmount * rate
        const symbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode

        // Format nicely based on currency
        let formattedPrice: string
        if (['CLP', 'COP', 'PYG', 'VES'].includes(currencyCode)) {
          formattedPrice = `${symbol} ${Math.round(converted).toLocaleString('es')}`
        } else {
          formattedPrice = `${symbol} ${converted.toFixed(2).replace('.', ',')}`
        }

        if (!cancelled) {
          setInfo({
            localPrice: formattedPrice,
            currencyCode,
            currencySymbol: symbol,
            countryName,
            countryCode,
            isLoading: false,
            error: false,
          })
        }
      } catch (err) {
        if (!cancelled) {
          setInfo((prev) => ({ ...prev, isLoading: false, error: true }))
        }
      }
    }

    fetchConversion()
    return () => {
      cancelled = true
    }
  }, [usdAmount])

  return info
}
